import { uniqueId } from 'lodash'
import {
  computed, ComputedRef, isRef, Ref,
} from 'vue'

type ItemObjectType = {
  children: ItemObjectType[]
  [prop: string]: any
}
type ItemType = string | ItemObjectType

type ComputedData = {
  value: any
  text: string
  id: string
  children?: any[]
}

export type ExtendItem = {
  [prop: string]: any
  computed: ComputedData
}

export interface Options {
  items: ItemType[]
  parseChildren: boolean
  itemToKey: (item: ItemType) => string
  itemToName: (item: ItemType) => string
  itemToValue: (item: ItemType) => unknown
  sortFn?: (item: ExtendItem[]) => ExtendItem[]
  filterFn?: (item: ExtendItem) => ExtendItem[]
  onSelect?: (...args: any) => any
}

const defaultOptions: Omit<Options, 'items'> = {
  parseChildren: false,
  itemToKey: (item: ItemType) => {
    if (typeof item === 'string') {
      return item
    }
    return item.id || uniqueId('downshift-')
  },
  itemToName: (item: ItemType) => {
    if (typeof item === 'string') {
      return item
    }
    return item.name
  },
  itemToValue: (item: ItemType) => {
    if (typeof item === 'string') {
      return item
    }
    return item.value
  },
}

type IdentMap = {
  [index: string]: unknown
}
interface UseDownShiftType {
  extItems: ComputedRef<ExtendItem[]>
  itemNameByIdentity: ComputedRef<IdentMap>
  isEqual: (option: ExtendItem, value: ItemType) => boolean
  options: ComputedRef<Options>
}

export default function useDownShift(
  userOptions: Pick<Options, 'items'> | ComputedRef<Pick<Options, 'items'>>,
  showPopover: Ref<boolean>,
): UseDownShiftType {
  const options = computed(() => ({
    ...defaultOptions,
    ...(isRef(userOptions) ? userOptions.value : userOptions),
  }))

  function selectItem(item: any, event: any, context: any = {}) {
    let isBreak = false

    if (!isBreak && options.value.onSelect) {
      isBreak = options.value.onSelect(item, event, context)
    }

    if (!isBreak) {
      showPopover.value = false
    }
  }

  function getComputeItem(i: ItemType) {
    let extra = {}
    if (typeof i !== 'string' && options.value.parseChildren) {
      const { children = [] } = i
      extra = {
        children: (children || []).map(c => getComputeItem(c)),
      }
    }
    return {
      ...(typeof i === 'string' ? {} : i),
      methods: {
        // @ts-ignore
        onSelect: (...args: any[]) => selectItem?.(...args),
      },
      computed: {
        value: options.value.itemToValue(i),
        text: options.value.itemToName(i),
        id: options.value.itemToKey(i),
        ...extra,
      },
    }
  }

  const sourceItems = computed(() => {
    const items = options.value.items.map(getComputeItem)
    return items
  })

  const itemNameByIdentity = computed(() => {
    const items = sourceItems.value.reduce((acc, item) => {
      if (typeof item.computed.value === 'string') {
        acc[item.computed.value] = item.computed.text
      }
      return acc
    }, {} as IdentMap)
    return items
  })

  function isEqual(option: ExtendItem, value: ItemObjectType) {
    if (typeof value === 'string') {
      return option.computed.value === value
    }
    return option.computed.id === options.value.itemToKey(value)
  }

  return {
    extItems: sourceItems,
    itemNameByIdentity,
    isEqual,
    options,
  }
}
